import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, TrendingUp, Users, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Reports = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      const data = await reportsAPI.getWeekly();
      setReport(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching report:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) {
    return <div className="p-8"><p className="font-manrope text-gray-600">Loading report...</p></div>;
  }

  const sourceData = report?.top_sources?.map(item => ({
    name: item.source,
    leads: item.count
  })) || [];

  const cityData = report?.top_cities?.map(item => ({
    name: item.city,
    leads: item.count
  })) || [];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-barlow text-4xl font-bold uppercase mb-2">Weekly Report</h1>
        <p className="font-manrope text-gray-600">
          {new Date(report?.week_start).toLocaleDateString()} - {new Date(report?.week_end).toLocaleDateString()}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">Total Leads</CardTitle>
            <Users className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-barlow font-bold">{report?.total_leads || 0}</div>
            <p className="text-xs text-gray-500 mt-1">This week</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">New Leads</CardTitle>
            <TrendingUp className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-barlow font-bold">{report?.new_leads || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Uncontacted</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">Booked Jobs</CardTitle>
            <BarChart3 className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-barlow font-bold">{report?.booked_jobs || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Confirmed</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">Revenue</CardTitle>
            <DollarSign className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-barlow font-bold">${(report?.estimated_revenue || 0).toLocaleString()}</div>
            <p className="text-xs text-gray-500 mt-1">Estimated</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">Leads by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sourceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="leads" fill="#FF8C00" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">Top Cities</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="leads" fill="#00BFFF" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
